import React, { useState, useEffect } from "react";
import { Box } from "@chakra-ui/react";
import { Header } from "build-ts/metadata";
import { ScheduledJobProto } from "build-ts/schedule";
import { getScheduledJob, putScheduledJob } from "services/ScheduleServices";
import showToast from "hooks/useCustomToast";
import WorkflowDefinition from "modules/analyst/components/WorkflowDefinition";

interface WorkflowDefinitionContainerProps {
  jobName: string;
  setJobName: (value: string) => void;
  selectedJob?: Header;
  /** Pre-fetched full proto — skips the getScheduledJob call when provided. */
  initialJobDetails?: ScheduledJobProto;
  /** Fires when the user confirms a name edit — triggers a name-only API save. */
  triggerNameSave?: boolean;
  setTriggerNameSave?: (value: boolean) => void;
  setDarwinName: (value: string) => void;
  onViewExecutionLog?: () => void;
  /** Called after any successful save (schedule or name) so the parent can run post-save side effects. */
  onWorkflowSaved?: (savedJob?: ScheduledJobProto) => void;
  /** When true, description, instructions and guidance fields become editable. */
  isEditMode?: boolean;
  /** Called to exit edit mode (cancel or after save). */
  setIsEditMode?: (v: boolean) => void;
  /** Increments to trigger a re-fetch of workflow details (e.g. after agent updates guidance). */
  refreshCounter?: number;
  /**
   * Ref that this container fills with its `handleSaveAll` function so the parent
   * (JobDetailView) can trigger a save from the header Save button.
   */
  saveHandlerRef?: React.MutableRefObject<(() => Promise<void>) | null>;
  /** Lets the parent mirror the isSavingAll state for the header Save button. */
  onSavingChange?: (saving: boolean) => void;
}

const WorkflowDefinitionContainer: React.FC<WorkflowDefinitionContainerProps> = ({
  jobName,
  setJobName,
  selectedJob,
  initialJobDetails,
  triggerNameSave = false,
  setTriggerNameSave,
  setDarwinName,
  onViewExecutionLog,
  onWorkflowSaved,
  isEditMode = false,
  setIsEditMode,
  refreshCounter = 0,
  saveHandlerRef,
  onSavingChange,
}) => {
  const [trigger, setTrigger] = useState<string>("");
  const [reportingInstructions, setReportingInstructions] = useState<string>("");
  const [selectedJobDetails, setSelectedJobDetails] = useState<ScheduledJobProto | undefined>(undefined);

  // Schedule state kept in sync with CronBuilder
  const [isActive, setIsActive] = useState<boolean>(false);
  const [cronExpression, setCronExpression] = useState<string>("");

  // Drafts for inline edit mode
  const [triggerDraft, setTriggerDraft] = useState<string>("");
  const [reportingDraft, setReportingDraft] = useState<string>("");

  const fetchJobDetails = async (selectedJobId: string) => {
    const response = await getScheduledJob(selectedJobId, false);
    if (response) {
      setSelectedJobDetails(response);
      setJobName(response.header?.name || "");
      setTrigger(response.scheduled_analysis?.trigger || "");
      setReportingInstructions(response.scheduled_analysis?.reporting_instructions || "");
      setCronExpression(response.cron_expr || "");
      setIsActive(response.is_active || false);
      setDarwinName(response.scheduled_analysis?.darwin_name || "");
    }
  };

  // Name-only save — updates just the header name, preserving all other job fields
  const handleSaveNameOnly = async () => {
    if (!selectedJobDetails?.header?.id) return;
    if (!jobName.trim()) {
      showToast("Workflow name cannot be empty", "", "warning");
      return;
    }
    const job = ScheduledJobProto.create({
      ...selectedJobDetails,
      header: { ...selectedJobDetails.header, name: jobName.trim() },
    });
    const response = await putScheduledJob(job);
    if (response) {
      setSelectedJobDetails(response);
      showToast("Workflow name updated", "", "success");
    } else {
      showToast("Failed to update workflow name", "", "error");
    }
  };

  // Save all editable fields (description + instructions) and trigger agent update
  const handleSaveAll = async () => {
    if (!selectedJobDetails?.header?.id) return;
    onSavingChange?.(true);
    try {
      const job = ScheduledJobProto.create({
        ...selectedJobDetails,
        scheduled_analysis: {
          ...selectedJobDetails.scheduled_analysis,
          trigger: triggerDraft,
          reporting_instructions: reportingDraft,
        },
      });
      const response = await putScheduledJob(job);
      if (response) {
        setSelectedJobDetails(response);
        setTrigger(response.scheduled_analysis?.trigger || "");
        setReportingInstructions(response.scheduled_analysis?.reporting_instructions || "");
        showToast("Workflow saved", "", "success");
        setIsEditMode?.(false);
        onWorkflowSaved?.(response);
      } else {
        showToast("Failed to save workflow", "", "error");
      }
    } finally {
      onSavingChange?.(false);
    }
  };

  // Keep the ref current so JobDetailView's header Save button always calls the latest handler
  useEffect(() => {
    if (saveHandlerRef) saveHandlerRef.current = handleSaveAll;
  });

  // Watch for triggerNameSave — fires when user confirms a name edit on an existing job
  useEffect(() => {
    if (triggerNameSave) {
      handleSaveNameOnly();
      setTriggerNameSave?.(false);
    }
  }, [triggerNameSave]);

  // Fetch job details from API if not provided in initialJobDetails
  useEffect(() => {
    if (selectedJob?.id) {
      if (initialJobDetails && initialJobDetails.header?.id === selectedJob.id) {
        setSelectedJobDetails(initialJobDetails);
        setJobName(initialJobDetails.header?.name || "");
        setTrigger(initialJobDetails.scheduled_analysis?.trigger || "");
        setReportingInstructions(initialJobDetails.scheduled_analysis?.reporting_instructions || "");
        setCronExpression(initialJobDetails.cron_expr || "");
        setIsActive(initialJobDetails.is_active || false);
        setDarwinName(initialJobDetails.scheduled_analysis?.darwin_name || "");
      } else {
        fetchJobDetails(selectedJob.id);
      }
    } else {
      setJobName("");
      setTrigger("");
      setReportingInstructions("");
      setCronExpression("");
      setIsActive(false);
      setSelectedJobDetails(undefined);
    }
  }, [selectedJob?.id]);

  // Re-fetch after the background agent finishes updating guidance
  useEffect(() => {
    if (refreshCounter > 0 && selectedJob?.id) {
      fetchJobDetails(selectedJob.id);
    }
  }, [refreshCounter]);

  // Initialise drafts whenever edit mode is entered
  useEffect(() => {
    if (isEditMode) {
      setTriggerDraft(trigger);
      setReportingDraft(reportingInstructions);
    }
  }, [isEditMode]);

  return (
    <Box flex="1" minH={0} minW={0} display="flex" flexDirection="column" overflow="hidden">
      <WorkflowDefinition
        trigger={trigger}
        reportingInstructions={reportingInstructions}
        cronExpression={cronExpression}
        setCronExpression={setCronExpression}
        isActive={isActive}
        setIsActive={setIsActive}
        selectedJobDetails={selectedJobDetails}
        onViewExecutionLog={onViewExecutionLog}
        onWorkflowSaved={onWorkflowSaved}
        isEditMode={isEditMode}
        triggerDraft={triggerDraft}
        setTriggerDraft={setTriggerDraft}
        reportingDraft={reportingDraft}
        setReportingDraft={setReportingDraft}
      />
    </Box>
  );
};

export default WorkflowDefinitionContainer;
